/**
 * TaskCard — carte d'une tâche dans la matrice d'Eisenhower.
 *
 * Intègre dnd-kit/useSortable pour être glissable entre quadrants.
 * La bordure gauche est colorée selon le quadrant courant.
 */
import { memo } from 'react';
import type { JSX } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Mail,
  Phone,
  Pencil,
  Sparkles,
  Inbox,
  Play,
  Check,
  Clock,
  GripVertical,
} from 'lucide-react';
import type { Task, TaskSource, Quadrant } from '@store/types';
import { UniqueBadge } from '@components/UniqueBadge';
import { AIScoreBadge } from '@components/AIScoreBadge';
import { formatDueLabel, getDueUrgency } from '@utils/dateUtils';

export interface TaskCardProps {
  task: Task;
  onStart?: (task: Task) => void;
  onComplete?: (task: Task) => void;
}

const quadrantBorder: Record<Quadrant, string> = {
  Q1: 'var(--color-urgent)',
  Q2: 'var(--color-important)',
  Q3: 'var(--color-normal)',
  Q4: 'var(--color-defer)',
};

const sourceIcon: Record<TaskSource, JSX.Element> = {
  email: <Mail size={12} />,
  call: <Phone size={12} />,
  manual: <Pencil size={12} />,
  ai: <Sparkles size={12} />,
  inherited: <Inbox size={12} />,
};

const sourceLabel: Record<TaskSource, string> = {
  email: 'Email',
  call: 'Appel',
  manual: 'Manuel',
  ai: 'IA',
  inherited: 'Hérité',
};

const urgencyColor: Record<ReturnType<typeof getDueUrgency>, string> = {
  late: 'var(--color-urgent)',
  soon: 'var(--color-urgent)',
  today: '#E6A800',
  later: 'var(--color-normal)',
  none: 'var(--color-defer)',
};

function TaskCardInner({ task, onStart, onComplete }: TaskCardProps): JSX.Element {
  const urgency = getDueUrgency(task.dueAt);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id, data: { quadrant: task.quadrant } });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    borderLeft: `4px solid ${quadrantBorder[task.quadrant]}`,
  };

  return (
    <article
      ref={setNodeRef}
      style={style}
      className="bg-white rounded-card border border-[color:var(--color-border)] p-4 shadow-card hover:shadow-hover transition-unique"
    >
      <header className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-start gap-1.5 flex-1 min-w-0">
          <button
            type="button"
            className="touch-none cursor-grab active:cursor-grabbing text-[color:var(--color-text-medium)] hover:text-[color:var(--color-primary-dark)] mt-0.5"
            aria-label="Glisser pour déplacer"
            {...attributes}
            {...listeners}
          >
            <GripVertical size={14} />
          </button>
          <h4 className="text-sm font-semibold leading-snug text-[color:var(--color-text-dark)] m-0 flex-1">
            {task.title}
          </h4>
        </div>
        <UniqueBadge tone="gold" icon={sourceIcon[task.source]}>
          {sourceLabel[task.source]}
        </UniqueBadge>
      </header>

      {task.description && (
        <p className="text-xs text-[color:var(--color-text-medium)] mb-3 line-clamp-2 pl-5">
          {task.description}
        </p>
      )}

      <div className="flex items-center justify-between gap-2 mb-3 pl-5">
        <AIScoreBadge score={task.aiScore} compact />
        <span
          className="inline-flex items-center gap-1 text-[11px] font-medium"
          style={{ color: urgencyColor[urgency] }}
        >
          <Clock size={12} />
          {formatDueLabel(task.dueAt)}
        </span>
      </div>

      <div className="flex items-center justify-end gap-1.5 pt-2 border-t border-[color:var(--color-border)]">
        <button
          type="button"
          onClick={() => onStart?.(task)}
          className="unique-btn-ghost !py-1 !px-2 text-xs inline-flex items-center gap-1"
          aria-label={`Démarrer la tâche : ${task.title}`}
        >
          <Play size={13} />
          Focus
        </button>
        <button
          type="button"
          onClick={() => onComplete?.(task)}
          className="unique-btn-ghost !py-1 !px-2 text-xs inline-flex items-center gap-1"
          aria-label={`Terminer la tâche : ${task.title}`}
        >
          <Check size={13} />
          Terminer
        </button>
      </div>
    </article>
  );
}

export const TaskCard = memo(TaskCardInner);
